import { trpc } from "@/api/client";
import { setEventTracking } from "@/utils";
import { useGetCep } from "@/utils/cep/use-get-cep";
import { cnpjValidator } from "@/utils/cnpj-validator";
import dispatchToast from "@/utils/dispatch-toast";
import { useSelectedCompany } from "@flash-tecnologia/hros-web-utility";
import { onlyNumbers } from "@utils/only-numbers";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { v4 as uuidv4 } from "uuid";
import * as yup from "yup";
import { REQUIRED_FORM_FIELD_ERROR } from "../constants";
import { Address, Company, NewCompany, TrackingEvents } from "../types";

const INVALID_REGISTRATION_NUMBER = "CNPJ inválido.";
const REGISTRATION_NUMBER_ALREADY_ADDED = "Empresa já cadastrada.";
const INVALID_ZIP_CODE = "CEP inválido.";

const BLANK_ADDRESS: Address = {
  zipCode: "",
  number: "",
  city: "",
  state: "",
  street: "",
  complement: "",
  district: "",
};

export const getValidationSchema = (
  companies: { registrationNumber: string }[]
) =>
  yup.object({
    registrationNumber: yup
      .string()
      .required(REQUIRED_FORM_FIELD_ERROR)
      .test("valid-cnpj", INVALID_REGISTRATION_NUMBER, (value) => {
        if (!value) return true;
        const digits = onlyNumbers(value);
        return cnpjValidator(digits);
      })
      .test("already-exists", REGISTRATION_NUMBER_ALREADY_ADDED, (value) => {
        if (!value) return true;
        const digits = onlyNumbers(value);

        return (
          !digits || !companies.some((m) => m.registrationNumber === digits)
        );
      }),
    legalName: yup.string().required(REQUIRED_FORM_FIELD_ERROR),
    name: yup.string(),
    address: yup.object({
      zipCode: yup.string().required(REQUIRED_FORM_FIELD_ERROR),
      number: yup.string().required(REQUIRED_FORM_FIELD_ERROR),
      city: yup.string().required(REQUIRED_FORM_FIELD_ERROR),
      state: yup.string().required(REQUIRED_FORM_FIELD_ERROR),
      street: yup.string().required(REQUIRED_FORM_FIELD_ERROR),
      district: yup.string().required(REQUIRED_FORM_FIELD_ERROR),
      complement: yup.string(),
    }),
    termsAccepted: yup.boolean().oneOf([true], REQUIRED_FORM_FIELD_ERROR),
  });

interface ManageCompaniesContextType {
  addCompanyLoading: boolean;
  address: Address;
  mainCompany?: Company;
  newCompanies: NewCompany[];
  legalName: string;
  name: string;
  registrationNumber?: string;
  filteredCompanies: {
    id: string;
    registrationNumber: string;
    legalName: string;
    deleteEnabled?: boolean;
  }[];
  isDeleteCompanyModalOpen: boolean;
  isFormLoading: boolean;
  isGenerateContractLoadingModalOpen: boolean;
  errors?: {
    registrationNumber?: string;
    legalName?: string;
    address?: {
      zipCode?: string;
      street?: string;
      number?: string;
      complement?: string;
      district?: string;
      state?: string;
      city?: string;
    };
    termsAccepted?: string;
  };

  isFormDisabled: boolean;
  setIsFormDisabled: (value: boolean) => void;
  shouldShakeAddCompanyButton?: boolean;
  termsAccepted: boolean;
  totalCompaniesAdded: number;

  onAddCompanyClick: () => Promise<void>;
  onAddressComplementBlur: () => void;
  onAddressComplementChange: (value: string) => void;
  onAddressNumberBlur: () => void;
  onAddressNumberChange: (value: string) => void;
  onDeleteCompanyClick: (memberId: string) => void;
  onDeleteCompanyModalClose: () => void;
  onDeleteCompanySubmit: () => void;
  onGenerateContractLoadingModalClose: () => void;
  onGetContractClick?: () => Promise<void>;
  onLegalNameBlur: () => void;
  onLegalNameChange: (value: string) => void;
  onNameChange: (value: string) => void;
  onRegistrationNumberBlur: () => void;
  onRegistrationNumberChange: (value: string) => void;
  onSearchCompaniesTermChange: (value?: string) => void;
  onTermsAcceptedChange: (value: boolean) => void;
  onZipCodeBlur: () => void;
  onZipCodeChange: (value: string) => void;
}

const ManageCompaniesContext = createContext<ManageCompaniesContextType | null>(
  null
);

interface ManageCompaniesProviderProps {
  children: ReactNode;
}

export const ManageCompaniesProvider: React.FC<
  ManageCompaniesProviderProps
> = ({ children }) => {
  const [newCompanies, setNewCompanies] = useState<NewCompany[]>([]);
  const [addCompanyLoading, setAddCompanyLoading] = useState<boolean>(false);
  const [
    isGenerateContractLoadingModalOpen,
    setGenerateContractLoadingModalOpen,
  ] = useState<boolean>(false);
  const [companyIdToDelete, setCompanyIdToDelete] = useState<string>();

  const [address, setAddress] = useState<Address>(BLANK_ADDRESS);
  const [legalName, setLegalName] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [registrationNumber, setRegistrationNumber] = useState<string>("");
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);

  const [searchCompaniesTerm, setSearchCompaniesTerm] = useState<string>();

  const [errors, setErrors] = useState<{
    termsAccepted?: string;
    registrationNumber?: string;
    legalName?: string;
    address?: {
      zipCode?: string;
      street?: string;
      number?: string;
      complement?: string;
      district?: string;
      state?: string;
      city?: string;
    };
  }>();

  const [isFormDisabled, setIsFormDisabled] = useState<boolean>(false);
  const [isDeleteCompanyModalOpen, setIsDeleteCompanyModalOpen] =
    useState(false);

  const {
    selectedCompany: { id: mainCompanyId },
  } = useSelectedCompany();

  const { getCep } = useGetCep();

  const { data: mainCompany, isFetching: isGetMainCompanyLoading } =
    trpc.getCompany.useQuery(
      {
        companyId: mainCompanyId,
      },
      {
        enabled: !!mainCompanyId,
      }
    );

  const {
    data: economicGroupCompanies,
    isFetching: isGetEconomicGroupCompaniesLoading,
  } = trpc.getEconomicGroupCompanies.useQuery();

  const { mutateAsync: generateContract } = trpc.addCompanyTerm.useMutation({
    onError: () => {
      dispatchToast({
        type: "error",
        content:
          "Ocorreu um erro ao gerar o contrato. Tente novamente mais tarde.",
      });
    },
  });

  const handleZipCodeBlur = async () => {
    if (!address.zipCode) {
      setErrors((prev) => ({
        ...prev,
        address: { ...prev?.address, zipCode: REQUIRED_FORM_FIELD_ERROR },
      }));
      return;
    }

    if (address.zipCode.length < 8) {
      setErrors((prev) => ({
        ...prev,
        address: { ...prev?.address, zipCode: INVALID_ZIP_CODE },
      }));
      return;
    }

    try {
      const result = await getCep(address.zipCode);

      if (!result?.state) {
        setErrors((prev) => ({
          ...prev,
          address: { ...prev?.address, zipCode: INVALID_ZIP_CODE },
        }));
        setAddress({ ...BLANK_ADDRESS, zipCode: address.zipCode });
        return;
      }

      setErrors((prev) => ({
        ...prev,
        address: { ...prev?.address, zipCode: undefined },
      }));
      setAddress({
        zipCode: address.zipCode,
        number: address.number,
        city: result.city,
        state: result.state,
        street: result.street,
        complement: result.complement || "",
        district: result.district,
      });
    } catch (err) {
      setAddress({ ...BLANK_ADDRESS, zipCode: address.zipCode });
      setErrors((prev) => ({
        ...prev,
        address: { ...prev?.address, zipCode: INVALID_ZIP_CODE },
      }));
    }
  };

  const handleZipCodeChange = (value: string) => {
    if (errors?.address?.zipCode)
      setErrors((prev) => ({
        ...prev,
        address: { ...prev?.address, zipCode: undefined },
      }));

    const numericZipCode = onlyNumbers(value);
    if (numericZipCode.length < 9) {
      setAddress({ ...BLANK_ADDRESS, zipCode: numericZipCode });
    }
  };

  useEffect(() => {
    if (address.zipCode.length === 8) handleZipCodeBlur();
  }, [address.zipCode]);

  const allCompanies = useMemo(() => {
    return (economicGroupCompanies || [])
      .map(({ registrationNumber }) => {
        return {
          registrationNumber,
        };
      })
      .concat(
        ...newCompanies.map(({ registrationNumber }) => {
          return {
            registrationNumber,
          };
        })
      );
  }, [economicGroupCompanies, newCompanies]);

  const handleAddCompanyClick = async () => {
    setAddCompanyLoading(true);
    let newErrors: Record<string, any> = {};

    try {
      await getValidationSchema(allCompanies).validate(
        {
          registrationNumber,
          legalName,
          name,
          address,
          termsAccepted,
        },
        { abortEarly: false }
      );
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        newErrors = error.inner.reduce((acc, curr) => {
          if (!curr.path) return acc;
          const parts = curr.path.split(".");
          if (parts[0] === "address" && parts.length > 1) {
            acc.address = acc.address || {};
            (acc.address as Record<string, string>)[parts.slice(1).join(".")] =
              curr.message;
          } else {
            (acc as Record<string, string>)[curr.path] = curr.message;
          }
          return acc;
        }, {} as Record<string, any>);
        setErrors(newErrors);
      }
    } finally {
      setAddCompanyLoading(false);
    }

    if (Object.values(newErrors).length) {
      return;
    }

    if (
      !registrationNumber ||
      !legalName ||
      !address.street ||
      !address.number ||
      !address.zipCode ||
      !address.city ||
      !address.state ||
      !address.district
    ) {
      return;
    }

    setNewCompanies((prev) => [
      ...prev,
      {
        id: uuidv4(),
        address,
        registrationNumber,
        legalName,
        name,
      },
    ]);
    resetForm();
  };

  const handleDeleteCompanyClick = (companyId: string) => {
    setEventTracking(TrackingEvents.SETUP_ADD_COMPANIES_DELETE_CLICKED);
    setCompanyIdToDelete(companyId);
    setIsDeleteCompanyModalOpen(true);
  };

  const handleDeleteCompanyModalClose = () =>
    setIsDeleteCompanyModalOpen(false);

  const handleDeleteCompanySubmit = () => {
    setNewCompanies((prev) =>
      prev.filter(({ id }) => id !== companyIdToDelete)
    );
    setCompanyIdToDelete(undefined);
    setIsDeleteCompanyModalOpen(false);
  };

  const handleAddressComplementBlur = () => {
    if (!address.number) {
      setErrors((prev) => ({
        ...prev,
        address: {
          ...prev?.address,
          complement: REQUIRED_FORM_FIELD_ERROR,
        },
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        address: { ...prev?.address, complement: undefined },
      }));
    }
  };

  const handleAddressComplementChange = (value: string) => {
    setAddress((prev) => ({ ...prev, complement: value }));
  };

  const handleAddressNumberBlur = () => {
    if (!address.number) {
      setErrors((prev) => ({
        ...prev,
        address: {
          ...prev?.address,
          number: REQUIRED_FORM_FIELD_ERROR,
        },
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        address: { ...prev?.address, number: undefined },
      }));
    }
  };

  const handleAddressNumberChange = (value: string) => {
    setAddress((prev) => ({ ...prev, number: value }));
  };

  const handleGenerateContractClick = async () => {
    setGenerateContractLoadingModalOpen(true);
    try {
      await handleGenerateContract();
    } finally {
      setGenerateContractLoadingModalOpen(false);
    }
  };

  const handleGenerateContractLoadingModalClose = () =>
    setGenerateContractLoadingModalOpen(false);

  const handleGenerateContract = async () => {
    let newErrors: Record<string, any> = {};

    try {
      await getValidationSchema(allCompanies).validate(
        {
          registrationNumber,
          legalName,
          name,
          address,
        },
        { abortEarly: false }
      );
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        newErrors = error.inner.reduce((acc, curr) => {
          if (!curr.path) return acc;
          const parts = curr.path.split(".");
          if (parts[0] === "address" && parts.length > 1) {
            acc.address = acc.address || {};
            (acc.address as Record<string, string>)[parts.slice(1).join(".")] =
              curr.message;
          } else {
            (acc as Record<string, string>)[curr.path] = curr.message;
          }
          return acc;
        }, {} as Record<string, any>);
        setErrors(newErrors);
      }
    }

    if (Object.values(newErrors).length) {
      dispatchToast({
        type: "error",
        content: "Preencha todos os dados da empresa para gerar o contrato",
      });
      return;
    }

    if (!mainCompany) {
      dispatchToast({
        type: "error",
        content:
          "Ocorreu um erro ao obter os dados de sua empresa. Tente novamente mais tarde.",
      });
      return;
    }

    const contract = await generateContract({
      companies: [
        {
          address: {
            city: address.city,
            complement: address.complement,
            district: address.district,
            number: `${address.number}`,
            state: address.state,
            street: address.street,
            zipCode: address.zipCode,
          },
          legalName,
          registrationNumber,
        },
      ],
      mainCompany: {
        address: {
          city: mainCompany.address.city ?? "",
          complement: mainCompany.address.complement ?? "",
          district: mainCompany.address.district ?? "",
          number: mainCompany.address.number ?? "",
          state: mainCompany.address.state ?? "",
          street: mainCompany.address.street ?? "",
          zipCode: mainCompany.address.zipCode ?? "",
        },
        economicGroupId: mainCompany.economicGroupId ?? "",
        legalName: mainCompany.legalName ?? "",
        registrationNumber: mainCompany.registrationNumber ?? "",
        id: mainCompany.id ?? "",
      },
    });

    if (!contract) {
      dispatchToast({
        type: "error",
        content:
          "Ocorreu um erro ao gerar o contrato da empresa. Tente novamente mais tarde.",
      });
      return;
    }

    window.open(contract.url, "_blank");
  };

  const handleRegistrationNumberBlur = () => {
    setIsFormDisabled(false);
    if (!registrationNumber) {
      return setErrors((prev) => ({
        ...prev,
        registrationNumber: REQUIRED_FORM_FIELD_ERROR,
      }));
    }

    if (!cnpjValidator(registrationNumber)) {
      return setErrors((prev) => ({
        ...prev,
        registrationNumber: INVALID_REGISTRATION_NUMBER,
      }));
    }

    const registrationNumberDigits = onlyNumbers(registrationNumber);
    const alreadyAdded = allCompanies.some(
      (member) => member.registrationNumber === registrationNumberDigits
    );
    if (alreadyAdded) {
      setIsFormDisabled(true);
      return setErrors((prev) => ({
        ...prev,
        registrationNumber: REGISTRATION_NUMBER_ALREADY_ADDED,
      }));
    }

    setErrors((prev) => ({ ...prev, registrationNumber: undefined }));
  };

  const handleRegistrationNumberChange = (value: string) => {
    const digits = onlyNumbers(value);
    if (digits?.length > 14) {
      return;
    }
    setRegistrationNumber(digits);
  };

  const handleLegalNameChange = (value: string) => setLegalName(value);

  const handleLegalNameBlur = () => {
    if (!legalName) {
      setErrors((prev) => ({ ...prev, legalName: REQUIRED_FORM_FIELD_ERROR }));
    } else {
      setErrors((prev) => ({ ...prev, legalName: undefined }));
    }
  };

  const handleNameChange = (value: string) => setName(value);

  const handleTermsAcceptedChange = (value: boolean) => {
    setTermsAccepted(value);
    if (value) {
      setErrors((prev) => ({ ...prev, termsAccepted: undefined }));
    } else {
      setErrors((prev) => ({
        ...prev,
        termsAccepted: REQUIRED_FORM_FIELD_ERROR,
      }));
    }
  };

  const totalCompaniesAdded = allCompanies.length;

  const addedCompanies = (economicGroupCompanies || [])
    .map(({ id, legalName, registrationNumber }) => {
      return {
        id,
        legalName,
        registrationNumber,
        deleteEnabled: false,
      };
    })
    .concat(
      ...newCompanies.map(({ id, legalName, registrationNumber }) => {
        return {
          id,
          legalName,
          registrationNumber,
          deleteEnabled: true,
        };
      })
    )
    .filter((addedCompany) => {
      if (!searchCompaniesTerm) return true;

      return addedCompany?.legalName
        .toLowerCase()
        .includes(searchCompaniesTerm.toLowerCase());
    });

  const resetForm = () => {
    setAddress({
      zipCode: "",
      street: "",
      number: "",
      complement: "",
      district: "",
      state: "",
      city: "",
    });
    setLegalName("");
    setName("");
    setRegistrationNumber("");
    setTermsAccepted(false);
    setErrors({});
    setIsFormDisabled(false);
  };

  const values = {
    addCompanyLoading,
    address,
    filteredCompanies: addedCompanies,
    isGenerateContractLoadingModalOpen,
    isFormLoading:
      isGetEconomicGroupCompaniesLoading || isGetMainCompanyLoading,
    isDeleteCompanyModalOpen,
    newCompanies,
    legalName,
    mainCompany,
    name,
    registrationNumber,
    errors,

    isFormDisabled,
    setIsFormDisabled,
    termsAccepted,
    totalCompaniesAdded,

    onAddCompanyClick: handleAddCompanyClick,
    onAddressComplementBlur: handleAddressComplementBlur,
    onAddressComplementChange: handleAddressComplementChange,
    onAddressNumberBlur: handleAddressNumberBlur,
    onAddressNumberChange: handleAddressNumberChange,
    onDeleteCompanyClick: handleDeleteCompanyClick,
    onDeleteCompanyModalClose: handleDeleteCompanyModalClose,
    onDeleteCompanySubmit: handleDeleteCompanySubmit,
    onGenerateContractLoadingModalClose:
      handleGenerateContractLoadingModalClose,
    onGetContractClick: handleGenerateContractClick,
    onLegalNameBlur: handleLegalNameBlur,
    onLegalNameChange: handleLegalNameChange,
    onNameChange: handleNameChange,
    onRegistrationNumberBlur: handleRegistrationNumberBlur,
    onRegistrationNumberChange: handleRegistrationNumberChange,
    onSearchCompaniesTermChange: setSearchCompaniesTerm,
    onTermsAcceptedChange: handleTermsAcceptedChange,
    onZipCodeBlur: handleZipCodeBlur,
    onZipCodeChange: handleZipCodeChange,
  };

  return (
    <ManageCompaniesContext.Provider value={values}>
      {children}
    </ManageCompaniesContext.Provider>
  );
};

export const useManageCompaniesForm = () => {
  const context = useContext(ManageCompaniesContext);
  if (!context) {
    throw new Error(
      "useManageCompaniesForm must be used within a ManageCompaniesProvider"
    );
  }
  return context;
};
